import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Spinner } from '@guryou/html-components';
import { stateIsLoading, useCurrentUser, useGender, updateUserProfile } from '@guryou/redux';
import Agreements from './Agreements';
import UserImage from './UserImage';
import ChangePassword from './ChangePassword';

const EditProfile = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const update = useSelector(state => state.user.update);
  const genderOptions = useGender() || [];
  const { user } = useCurrentUser();
  const userLanguage = user?.userLanguage;

  const profileSchema = z.object({
    name: z.string().min(2, t('VALIDATIONS.usernameMin')),
    firstname: z.string().min(2, t('VALIDATIONS.firstnameMin')),
    lastname: z.string().min(2, t('VALIDATIONS.lastnameMin')),
    email: z.string().email(t('VALIDATIONS.emailInvalid')),
    phone: z.string().optional(),
    birthday: z.string().optional(),
    gender: z.string().optional(),
    address: z.string().optional(),
    country: z.string().optional(),
    city: z.string().optional(),
    postcode: z.string().optional(),
    userLanguage: z.string().optional(),
    agreements: z.any().optional(),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm({
    resolver: zodResolver(profileSchema),
  });

  useEffect(() => {
    if (user) {
      reset({
        ...user,
        birthday: user.birthday ? new Date(user.birthday).toISOString().split('T')[0] : '',
      });
    }
  }, [user, reset]);

  useEffect(() => {
    const lang = userLanguage;
    if (!lang) return;
    const normalized = String(lang)
      .toLowerCase()
      .startsWith('en')
      ? 'en'
      : 'it';
    if (i18n.language !== normalized) {
      i18n.changeLanguage(normalized);
    }
  }, [userLanguage, i18n]);

  const onSubmit = data => {
    if (data?.userLanguage) {
      const normalized = String(data.userLanguage)
        .toLowerCase()
        .startsWith('en')
        ? 'en'
        : 'it';
      if (i18n.language !== normalized) {
        i18n.changeLanguage(normalized);
      }
    }
    dispatch(updateUserProfile(data));
  };

  if (!user) {
    return <Spinner />;
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 sm:p-8">
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 space-y-8">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('PROFILE.photo')}</h3>
              <UserImage acc={user} />
            </div>
            <Agreements agreements={user?.agreements} register={register} />
            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('PROFILE.changePass')}</h3>
              <ChangePassword />
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="sm:col-span-2">
                <h3 className="text-lg font-medium text-gray-900">{t('PROFILE.personalInfo')}</h3>
              </div>
              <div>
                <label htmlFor="firstname" className="block mb-2 text-sm font-medium text-gray-900">
                  {t('GENERAL.name')}
                </label>
                <input
                  type="text"
                  id="firstname"
                  {...register('firstname')}
                  className={`bg-gray-50 border ${
                    errors.firstname ? 'border-red-500' : 'border-gray-300'
                  } text-gray-900 text-sm rounded-lg focus:border-[var(--feel-primary)] focus:ring-[var(--feel-primary)] block w-full p-2.5`}
                />
                {errors.firstname && <p className="mt-2 text-sm text-red-600">{errors.firstname.message}</p>}
              </div>
              <div>
                <label htmlFor="lastname" className="block mb-2 text-sm font-medium text-gray-900">
                  {t('GENERAL.surname')}
                </label>
                <input
                  type="text"
                  id="lastname"
                  {...register('lastname')}
                  className={`bg-gray-50 border ${
                    errors.lastname ? 'border-red-500' : 'border-gray-300'
                  } text-gray-900 text-sm rounded-lg focus:border-[var(--feel-primary)] focus:ring-[var(--feel-primary)] block w-full p-2.5`}
                />
                {errors.lastname && <p className="mt-2 text-sm text-red-600">{errors.lastname.message}</p>}
              </div>
              <div>
                <label htmlFor="name" className="block mb-2 text-sm font-medium text-gray-900">
                  {t('GENERAL.username')}
                </label>
                <input
                  type="text"
                  id="name"
                  {...register('name')}
                  className={`bg-gray-50 border ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  } text-gray-900 text-sm rounded-lg focus:border-[var(--feel-primary)] focus:ring-[var(--feel-primary)] block w-full p-2.5`}
                />
                {errors.name && <p className="mt-2 text-sm text-red-600">{errors.name.message}</p>}
              </div>
              <div>
                <label htmlFor="birthday" className="block mb-2 text-sm font-medium text-gray-900">
                  {t('GENERAL.dateofbirth')}
                </label>
                <input
                  type="date"
                  id="birthday"
                  {...register('birthday')}
                  className={`bg-gray-50 border ${
                    errors.birthday ? 'border-red-500' : 'border-gray-300'
                  } text-gray-900 text-sm rounded-lg focus:border-[var(--feel-primary)] focus:ring-[var(--feel-primary)] block w-full p-2.5`}
                />
              </div>
              <div>
                <label htmlFor="userLanguage" className="block mb-2 text-sm font-medium text-gray-900">
                  {t('GENERAL.language', 'Lingua')}
                </label>
                <select
                  id="userLanguage"
                  {...register('userLanguage')}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:border-[var(--feel-primary)] focus:ring-[var(--feel-primary)] block w-full p-2.5"
                >
                  <option value="it">Italiano</option>
                  <option value="en">English</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block mb-3 text-sm font-medium text-gray-900">{t('PROFILE.gender')}</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {genderOptions.map(option => (
                    <label
                      key={option.value}
                      htmlFor={`gender-${option.value}`}
                      className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors cursor-pointer group"
                    >
                      <div className="relative flex items-center h-5 mt-0.5">
                        <input
                          id={`gender-${option.value}`}
                          type="radio"
                          value={option.value}
                          {...register('gender')}
                          className={`w-5 h-5 border-gray-300 text-[var(--feel-primary)] focus:ring-2 focus:ring-[var(--feel-primary)] focus:ring-offset-0 cursor-pointer transition-colors`}
                        />
                      </div>
                      <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="sm:col-span-2 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">{t('PROFILE.contactInfo', 'Contact Information')}</h3>
              </div>
              <div>
                <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-900">
                  {t('GENERAL.email')}
                </label>
                <input
                  type="email"
                  id="email"
                  {...register('email')}
                  className={`bg-gray-50 border ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  } text-gray-900 text-sm rounded-lg focus:border-[var(--feel-primary)] focus:ring-[var(--feel-primary)] block w-full p-2.5`}
                />
                {errors.email && <p className="mt-2 text-sm text-red-600">{errors.email.message}</p>}
              </div>
              <div>
                <label htmlFor="phone" className="block mb-2 text-sm font-medium text-gray-900">
                  {t('GENERAL.phone')}
                </label>
                <input
                  type="tel"
                  id="phone"
                  {...register('phone')}
                  className={`bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:border-[var(--feel-primary)] focus:ring-[var(--feel-primary)] block w-full p-2.5`}
                />
              </div>

              <div className="sm:col-span-2 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">{t('PROFILE.addressInfo', 'Address')}</h3>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="address" className="block mb-2 text-sm font-medium text-gray-900">
                  {t('GENERAL.address')}
                </label>
                <input
                  type="text"
                  id="address"
                  {...register('address')}
                  className={`bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:border-[var(--feel-primary)] focus:ring-[var(--feel-primary)] block w-full p-2.5`}
                />
              </div>
              <div>
                <label htmlFor="city" className="block mb-2 text-sm font-medium text-gray-900">
                  {t('GENERAL.city')}
                </label>
                <input
                  type="text"
                  id="city"
                  {...register('city')}
                  className={`bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:border-[var(--feel-primary)] focus:ring-[var(--feel-primary)] block w-full p-2.5`}
                />
              </div>
              <div>
                <label htmlFor="postcode" className="block mb-2 text-sm font-medium text-gray-900">
                  {t('GENERAL.postalCode')}
                </label>
                <input
                  type="text"
                  id="postcode"
                  {...register('postcode')}
                  className={`bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:border-[var(--feel-primary)] focus:ring-[var(--feel-primary)] block w-full p-2.5`}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="country" className="block mb-2 text-sm font-medium text-gray-900">
                  {t('GENERAL.national')}
                </label>
                <input
                  type="text"
                  id="country"
                  {...register('country')}
                  className={`bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:border-[var(--feel-primary)] focus:ring-[var(--feel-primary)] block w-full p-2.5`}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="mt-8 pt-6 border-t border-gray-200 flex items-center justify-end gap-4">
          <button
            type="submit"
            disabled={!isDirty || isSubmitting || stateIsLoading(update)}
            className={`bg-[var(--feel-primary)] disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg min-w-[200px]`}
          >
            {t('PROFILE.save')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditProfile;
